import {UIAjaxWrapper} from '../../../shared/scripts/network/UIAjaxWrapper'
import {UIPopupManager} from '../../../shared/scripts/ui_popup/UIPopupManager'
import {UIMSceneEnum} from '../../../../shared/enums/UIMSceneEnums'
import {UIMMainPageController} from '../ui_html/main_page/UIMMainPageController'
import {UIMFullscreenPageController} from './UIMFullscreenPageController'
import {MatchType, ReplayInitiationData} from '@colonist/game-base-types'
import {UIReplayController} from '../../../shared/scripts/ui_replay/UIReplayController'
import {UIGameSpriteHandler} from '../../../shared/scripts/UIGameSpriteHandler'
import {socket} from '../../../shared/scripts/EntryShared'
import {getReplayDataUrl} from '../../../shared/scripts/ui_replay/ReplayUrlUtilities'
import {ClientUserState} from '../../../shared/scripts/ClientUserState'
import {UIMViewPageNavigationWarningPopup} from '../ui_popup/UIMViewPageNavigationWarningPopup'
import {uiManager} from '../EntryMobile'
import {navigateToPage} from '../../../shared/scripts/ui_utils/ClientUtils'
import {UIUrlParameterNames} from '../../../shared/scripts/ui_html/UIUrlController'
import {UIEntryUrlParameterController} from '../../../shared/scripts/UIEntryUrlParameterController'
import {UIViewProfilePopup} from '../../../shared/scripts/profile/UIViewProfilePopup'

export class UIMSingletonPageController extends UIMFullscreenPageController {
    static routesListener: (event: MouseEvent) => void
    private readonly mainpageController: UIMMainPageController

    constructor(mainpageController: UIMMainPageController) {
        super()
        this.mainpageController = mainpageController
        this.setLinkListeners()
        this.updatePageOnHashChange()
    }

    handleUrlRedirect(href: string): boolean {
        const pathname = UIMSingletonPageController.getSingletonPageUrl(href)
        const hash = href.split('#')[1] ?? ''
        if(!pathname) return false

        if(hash == `${UIUrlParameterNames.MainPage}=${UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE}`) {
            this.redirectToScene(UIMSceneEnum.Homepage)
            return true
        }

        if(hash.startsWith(`${UIUrlParameterNames.SelectMode}=`)) {
            const requestedMatchType = hash.split('=')[1] as MatchType

            this.redirectToScene(UIMSceneEnum.Homepage)
            void uiManager.goToMainPageAndSelectMatchType(requestedMatchType)
            return true
        }

        if(hash == `${UIUrlParameterNames.Spectate}=${UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE}`) {
            this.redirectToScene(UIMSceneEnum.Lobby)
            this.mainpageController.bodyController.lobbyController.tableController.headerController.openSpectateTab()
            return true
        }
        if(pathname.includes('/store') || pathname.includes('/checkout')) {
            this.redirectToScene(UIMSceneEnum.Store)
            if(hash) this.mainpageController?.bodyController?.storeController?.sidebarController?.openDefaultStorePage(`#${hash}`)
            return true
        }
        if(pathname.includes('/leaderboards')) {
            this.redirectToScene(UIMSceneEnum.Leaderboard)
            return true
        }
        if(pathname.includes('/profile')) {
            const showedProfilePopup = this.shouldOpenProfilePopup(href)
            if(!showedProfilePopup) {
                new UIMViewPageNavigationWarningPopup(() => {
                    if(uiManager?.mainpageController?.sceneController?.scene == UIMSceneEnum.Game) {
                        return navigateToPage(`/profile/${ClientUserState.userState?.username?.toLowerCase()}`)
                    } else {
                        this.redirectToScene(UIMSceneEnum.Profile)
                    }
                })
            }
            return true
        }
        if(pathname.includes('/replay')) {
            new UIMViewPageNavigationWarningPopup(() => {
                if(uiManager?.mainpageController?.sceneController?.scene == UIMSceneEnum.Game) {
                    return navigateToPage(href)
                }

                const replayDataUrl = getReplayDataUrl(href)
                this.loadReplay(replayDataUrl)
            })
            return true
        }

        if(pathname.includes('/about')) {
            this.openUrl('/about')
            return true
        }

        if(pathname.includes('/values-examples')) {
            this.openUrl('/values-examples')
            return true
        }

        if(pathname.includes('/values')) {
            this.openUrl('/values')
            return true
        }

        if(pathname.includes('/catan-rules')) {
            // eslint-disable-next-line no-restricted-syntax
            window.location.href = '/catan-rules'
            return true
        }

        if(pathname.includes('/community')) {
            this.openUrl('/community')
            return true
        }

        if(pathname.includes('/faq')) {
            this.openUrl('/faq')
            return true
        }

        if(pathname.includes('/privacy-policy')) {
            this.openUrl('/privacy-policy')
            return true
        }

        if(pathname.includes('/careers')) {
            this.openUrl('/careers')
            return true
        }

        if(pathname.includes('/terms')) {
            this.openUrl('/terms')
            return true
        }

        if(pathname.includes('/refund-policy')) {
            this.openUrl('/refund-policy')
            return true
        }

        if(pathname.includes('/press-kit')) {
            this.openUrl('/press-kit')
            return true
        }

        if(pathname.includes('/championship2025')) {
            this.openUrl('/championship2025')
            return true
        }

        if(pathname.includes('/championship2026')) {
            this.openUrl('/championship2026')
            return true
        }

        return false
    }

    // eslint-disable-next-line class-methods-use-this
    private shouldOpenProfilePopup(href: string): boolean {
        const profileUsername = UIMSingletonPageController.getProfileUsername(href)
        if(!profileUsername) return false
        if(profileUsername.toLowerCase() == ClientUserState.userState?.username?.toLowerCase()) return false

        UIViewProfilePopup.init(profileUsername)
        return true
    }

    static getProfileUsername(href: string): string | undefined {
        if(!href.includes('/profile')) return
        const fullUrl = href.startsWith('http') ? href : window.location.origin + href
        const url = new URL(fullUrl)
        const pathnameParts = url.pathname.split('/')
        const username = pathnameParts[pathnameParts.length - 1]
        if(!username) return
        return username
    }

    private setLinkListeners() {
        UIMSingletonPageController.routesListener = (event: MouseEvent) => {
            const clickedElement = event.target as HTMLElement
            if(clickedElement == undefined) return
            const href = UIMSingletonPageController.getElementHref(clickedElement)
            if(!href) return

            const pageUrl = UIMSingletonPageController.getSingletonPageUrl(href)
            if(!pageUrl) return

            event.preventDefault()
            this.mainpageController.headerController.hamburgerController.setCloseAction()

            if(pageUrl.startsWith('#')) {
                const hash = href.split('#')[1]
                if(hash) {
                    const elementToScrollTo = document.getElementById(hash)
                    elementToScrollTo?.scrollIntoView({behavior: 'smooth'})
                }
                return
            }

            if(pageUrl.includes('/static')) return
            if(pageUrl.includes('/dist')) return

            UIPopupManager.closeAllPopups()
            this.mainpageController?.headerController.notificationPanelController?.hideNotificationPanel()
            this.mainpageController?.bodyController?.modeSelectorDetailsController?.hide()

            const isRedirected = this.handleUrlRedirect(href)
            if(isRedirected) return

            this.openUrl(pageUrl)
        }

        document.addEventListener('click', UIMSingletonPageController.routesListener)
    }

    // eslint-disable-next-line class-methods-use-this
    removeLinkListener() {
        document.removeEventListener('click', UIMSingletonPageController.routesListener)
    }

    private loadReplay(replayUrl: string) {
        void UIGameSpriteHandler.continueAfterAssetsLoad(async() => {
            const replayDataUrl = replayUrl
            const replayDataResponse = await UIAjaxWrapper.getRequestHandler(replayDataUrl, undefined)

            const replayInitiationData = replayDataResponse?.json.data as ReplayInitiationData | undefined
            if(replayInitiationData === undefined) throw new Error('Missing replayData')

            this.redirectToScene(UIMSceneEnum.Game)
            const isLiveGame = false
            new UIReplayController(replayInitiationData, socket.actionController, isLiveGame)
        })
    }

    static getElementHref(clickedElement: HTMLElement) {
        const element = clickedElement.tagName
        const isATag = element == 'A'
        if(isATag) return clickedElement.getAttribute('href')

        return clickedElement.parentElement?.getAttribute('href')
    }

    static getSingletonPageUrl(href: string): string | undefined {
        const urlContainsSiteDomain = href.startsWith(window.location.origin)
        if(urlContainsSiteDomain) return new URL(href).pathname

        const isHash = href.startsWith('#')
        if(isHash) return href

        const urlIsRelative = href.startsWith('/')
        if(!urlIsRelative) return

        if(href.includes('catan-rules')) return
        if(href.includes('/auth/')) return
        return href
    }

    private redirectToScene(scene: UIMSceneEnum) {
        this.mainpageController.sceneController.loadScene(scene)
        this.closePage()
        return
    }

    // eslint-disable-next-line class-methods-use-this
    private updatePageOnHashChange() {
        window.onhashchange = (hashChangeEvent: HashChangeEvent) => {
            const oldUrl = new URL(hashChangeEvent.oldURL)
            const newUrl = new URL(hashChangeEvent.newURL)

            const isTheSameHash = oldUrl.hash.toLocaleLowerCase() == newUrl.hash.toLocaleLowerCase()
            if(isTheSameHash) return

            const hashesNotEmpty = oldUrl.hash != '' && newUrl.hash != ''

            if(!hashesNotEmpty) return
            location.reload()
        }
    }
}
